import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip,
  Button,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  MonetizationOn as MonetizationOnIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  WorkHistory as WorkHistoryIcon
} from '@mui/icons-material';
import RevenueMonthlyComparison from './RevenueMonthlyComparison';

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(amount);
};

const formatPercent = (p) => (p == null ? '—' : `${p > 0 ? '+' : ''}${p.toFixed(1).replace('.', ',')}%`);

// Theme màu chuyên nghiệp
const dashboardTheme = {
  primary: '#1e3a8a',
  secondary: '#059669',
  accent: '#7c3aed',
  warning: '#d97706',
  error: '#dc2626',
  success: '#059669',
  info: '#0284c7',
  gradients: {
    primary: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    secondary: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    accent: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
    warning: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
    info: 'linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%)'
  }
};

const DashboardSummary = ({
  users = [],
  livestreams = [],
  userSummaries = [],
  summaryData,
  setSummaryData,
  selectedUser,
  setSelectedUser,
  summaryType,
  setSummaryType,
  summaryDate,
  setSummaryDate,
  customDateRange,
  setCustomDateRange,
  selectedPeriod,
  setSelectedPeriod,
  selectedUserForSummary,
  setSelectedUserForSummary,
  filteredSummaries = [],
  setFilteredSummaries,
  periodSummaries = [],
  fetchSummary,
  getPeriodSummaries,
  isLoading,
  setIsLoading,
  showNotification,
  isMobile
}) => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Số liệu so sánh theo tháng, do RevenueMonthlyComparison nạp rồi đẩy ngược lên
  // (dùng chung cho thẻ "Tổng doanh thu" để không phải gọi API 2 lần)
  const [comparison, setComparison] = useState(null);

  // Enhanced Stats Card Component
  const StatsCard = ({ 
    title, 
    value, 
    subtitle, 
    icon: Icon, 
    color, 
    gradient, 
    trend,
    trendValue,
    loading = false 
  }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: gradient,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        }
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
              {title}
            </Typography>
            {loading ? (
              <Box sx={{ my: 1 }}>
                <CircularProgress size={20} color="inherit" />
              </Box>
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 700, my: 1 }}>
                {value}
              </Typography>
            )}
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {subtitle}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
            <Icon sx={{ fontSize: 24 }} />
          </Avatar>
        </Box>
        
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            {trend === 'up' ? (
              <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} />
            )}
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {trendValue}
            </Typography>
          </Box>
        )}
      </CardContent>
      
      {/* Decorative background element */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.1)',
          zIndex: 1
        }}
      />
    </Card>
  );

  // Summary Table Component
  const SummaryTable = ({ data, title, loading }) => (
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e2e8f0'
    }}>
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid #e2e8f0',
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700, color: dashboardTheme.primary }}>
            {title}
          </Typography>
          <Tooltip title="Làm mới dữ liệu">
            <IconButton 
              onClick={() => setRefreshing(!refreshing)}
              disabled={loading}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600, color: dashboardTheme.primary }}>
                Nhân viên
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: dashboardTheme.primary }}>
                Giờ live
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: dashboardTheme.primary }}>
                Thu nhập
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: dashboardTheme.primary }}>
                Hiệu suất
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : data && data.length > 0 ? (
              data.map((item, index) => {
                const user = users.find(u => u._id === item.userId);
                const performanceScore = Math.min(100, (item.totalHours / 40) * 100); // Giả sử 40h là mục tiêu
                
                return (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:hover': { bgcolor: '#f8fafc' },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            mr: 2,
                            bgcolor: dashboardTheme.accent,
                            fontSize: 14
                          }}
                        >
                          {user?.fullName?.charAt(0) || 'N'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {user?.fullName || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user?.username || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${item.totalHours || 0}h`}
                        color={item.totalHours > 30 ? 'success' : item.totalHours > 15 ? 'warning' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color={dashboardTheme.success}>
                        {new Intl.NumberFormat('vi-VN', {
                          style: 'currency',
                          currency: 'VND'
                        }).format(item.totalEarnings || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ width: 60, mr: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={performanceScore}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                bgcolor: performanceScore > 75 ? dashboardTheme.success : 
                                        performanceScore > 50 ? dashboardTheme.warning : 
                                        dashboardTheme.error
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="caption" fontWeight={600}>
                          {Math.round(performanceScore)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <AssessmentIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Không có dữ liệu để hiển thị
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );

  // Calculate dashboard statistics
  const calculateStats = () => {
    const totalUsers = users.length;
    const totalHours = userSummaries.reduce((sum, user) => sum + (user.totalHours || 0), 0);
    const totalEarnings = userSummaries.reduce((sum, user) => sum + (user.totalEarnings || 0), 0);
    const activeUsers = userSummaries.filter(user => user.totalHours > 0).length;
    const avgHoursPerUser = totalUsers > 0 ? totalHours / totalUsers : 0;
    
    return {
      totalUsers,
      totalHours,
      totalEarnings,
      activeUsers,
      avgHoursPerUser
    };
  };

  const stats = calculateStats();

  // Xử lý khi thay đổi kỳ
  const handlePeriodChange = async (event) => {
    const newPeriod = event.target.value;
    setSelectedPeriod(newPeriod);
    setLoading(true);

    try {
      if (newPeriod === 'current') {
        if (selectedUserForSummary && Array.isArray(userSummaries)) {
          setFilteredSummaries(userSummaries.filter(summary => 
            summary.userId === selectedUserForSummary
          ));
        } else {
          setFilteredSummaries(Array.isArray(userSummaries) ? userSummaries : []);
        }
      } else if (newPeriod === 'previous') {
        if (!Array.isArray(periodSummaries)) {
          console.error('periodSummaries không phải là mảng:', periodSummaries);
          setFilteredSummaries([]);
        } else if (selectedUserForSummary) {
          setFilteredSummaries(periodSummaries.filter(summary => 
            summary.userId?._id === selectedUserForSummary || summary.userId === selectedUserForSummary
          ));
        } else {
          setFilteredSummaries(periodSummaries);
        }
      }
    } catch (error) {
      console.error('Lỗi khi xử lý thay đổi kỳ:', error);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý khi thay đổi user
  const handleUserSummaryChange = (event) => {
    const newUserId = event.target.value;
    setSelectedUserForSummary(newUserId);
    setLoading(true);

    try {
      if (selectedPeriod === 'current') {
        if (newUserId && Array.isArray(userSummaries)) {
          setFilteredSummaries(userSummaries.filter(summary => 
            summary.userId === newUserId
          ));
        } else {
          setFilteredSummaries(Array.isArray(userSummaries) ? userSummaries : []);
        }
      } else if (selectedPeriod === 'previous') {
        if (!Array.isArray(periodSummaries)) {
          console.error('periodSummaries không phải là mảng:', periodSummaries);
          setFilteredSummaries([]);
        } else if (newUserId) {
          setFilteredSummaries(periodSummaries.filter(summary => 
            summary.userId?._id === newUserId || summary.userId === newUserId
          ));
        } else {
          setFilteredSummaries(periodSummaries);
        }
      }
    } catch (error) {
      console.error('Lỗi khi xử lý thay đổi user:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cập nhật filteredSummaries khi data thay đổi
  useEffect(() => {
    try {
      if (selectedPeriod === 'current') {
        if (selectedUserForSummary && Array.isArray(userSummaries)) {
          setFilteredSummaries(userSummaries.filter(summary => 
            summary.userId === selectedUserForSummary
          ));
        } else {
          setFilteredSummaries(Array.isArray(userSummaries) ? userSummaries : []);
        }
      } else if (selectedPeriod === 'previous') {
        if (!Array.isArray(periodSummaries)) {
          console.error('periodSummaries không phải là mảng trong useEffect:', periodSummaries);
          setFilteredSummaries([]);
          return;
        }
        
        if (periodSummaries.length > 0) {
          if (selectedUserForSummary) {
            setFilteredSummaries(periodSummaries.filter(summary => 
              summary.userId?._id === selectedUserForSummary || summary.userId === selectedUserForSummary
            ));
          } else {
            setFilteredSummaries(periodSummaries);
          }
        }
      }
    } catch (error) {
      console.error('Lỗi trong useEffect khi cập nhật filteredSummaries:', error);
      setFilteredSummaries([]);
    }
  }, [userSummaries, periodSummaries, selectedPeriod, selectedUserForSummary, setFilteredSummaries]);

  // Hiển thị tên người dùng
  const getUserName = (userId) => {
    const user = users.find(u => u._id === userId);
    return user ? user.fullName : 'N/A';
  };

  // Tính tổng số tiền từ filteredSummaries
  const getTotalEarnings = () => {
    if (!filteredSummaries || !Array.isArray(filteredSummaries) || filteredSummaries.length === 0) return 0;
    
    return filteredSummaries.reduce((acc, curr) => {
      const earnings = curr.totalEarnings || 0;
      return acc + earnings;
    }, 0);
  };

  // Lấy thông tin khoảng thời gian
  const getPeriodInfo = () => {
    if (!filteredSummaries || filteredSummaries.length === 0) {
      return 'Không có dữ liệu';
    }

    const firstItem = filteredSummaries[0];
    const startDate = firstItem.periodStart || (firstItem.date ? new Date(firstItem.date) : null);
    const endDate = firstItem.periodEnd;
    
    if (!startDate) return 'Không có thông tin thời gian';
    
    const formatDate = (date) => {
      if (!date) return '';
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('vi-VN');
    };
    
    return `Từ ${formatDate(startDate)} đến ${formatDate(endDate)}`;
  };

  return (
    <Box>
      {/* Header với action buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: dashboardTheme.primary, mb: 1 }}>
            📊 Dashboard Tổng Quan
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Theo dõi hiệu suất và doanh thu livestream
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none' }}
          >
            Xuất báo cáo
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => setRefreshing(!refreshing)}
            sx={{ 
              textTransform: 'none',
              background: dashboardTheme.gradients.primary
            }}
          >
            Làm mới
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Tổng người dùng"
            value={stats.totalUsers}
            subtitle="Người dùng hoạt động"
            icon={PeopleIcon}
            gradient={dashboardTheme.gradients.primary}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Tổng giờ live"
            value={`${stats.totalHours}h`}
            subtitle="Trong kỳ này"
            icon={AccessTimeIcon}
            gradient={dashboardTheme.gradients.secondary}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Tổng doanh thu"
            value={comparison ? formatMoney(comparison.hero.current.totalGMV) : '—'}
            subtitle={comparison
              ? `GMV đã duyệt · ${comparison.hero.windowLabel.split(' so với ')[0]}`
              : 'Đang tải…'}
            icon={MonetizationOnIcon}
            gradient={dashboardTheme.gradients.accent}
            loading={!comparison}
            trend={comparison?.hero.gmvChangePercent == null
              ? null
              : comparison.hero.gmvChangePercent >= 0 ? 'up' : 'down'}
            trendValue={comparison
              ? `${formatPercent(comparison.hero.gmvChangePercent)} so với cùng kỳ tháng trước`
              : ''}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Trung bình giờ/người"
            value={`${Math.round(stats.avgHoursPerUser * 10) / 10}h`}
            subtitle="Hiệu suất trung bình"
            icon={AssessmentIcon}
            gradient={dashboardTheme.gradients.warning}
          />
        </Grid>
      </Grid>

      {/* Period Summary Section */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0'
      }}>
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid #e2e8f0',
          background: dashboardTheme.gradients.info,
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              💰 Thu nhập theo kỳ
            </Typography>
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedPeriod}
                  onChange={handlePeriodChange}
                  sx={{ 
                    color: 'white',
                    '& .MuiSelect-icon': { color: 'white' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' }
                  }}
                  disabled={loading}
                >
                  <MenuItem value="current">Kỳ hiện tại</MenuItem>
                  <MenuItem value="previous">Kỳ trước</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={selectedUserForSummary}
                  onChange={handleUserSummaryChange}
                  displayEmpty
                  sx={{ 
                    color: 'white',
                    '& .MuiSelect-icon': { color: 'white' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' }
                  }}
                  disabled={loading}
                >
                  <MenuItem value="">Tất cả nhân viên</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={30} color="inherit" />
            </Box>
          ) : (
            <>
              <Typography variant="h4" sx={{ fontWeight: 700, my: 1 }}>
                {new Intl.NumberFormat('vi-VN', {
                  style: 'currency',
                  currency: 'VND'
                }).format(getTotalEarnings())}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {getPeriodInfo()}
              </Typography>
            </>
          )}
        </Box>
      </Card>

      {/* So sánh doanh thu & hoa hồng theo tháng */}
      <Box sx={{ mb: 4 }}>
        <RevenueMonthlyComparison isMobile={isMobile} onLoaded={setComparison} />
      </Box>

      {/* Summary Table */}
      <SummaryTable
        data={filteredSummaries}
        title="Chi tiết thu nhập nhân viên"
        loading={loading}
      />

      {/* Quick Insights */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          <Alert 
            severity="info" 
            icon={<CalendarIcon />}
            sx={{ 
              borderRadius: 2,
              '& .MuiAlert-message': { fontSize: '0.95rem' }
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Thông tin chu kỳ tính lương
            </Typography>
            <Typography variant="body2">
              Chu kỳ tính lương từ ngày 10 tháng trước đến ngày 9 tháng này. 
              Hệ thống sẽ tự động lưu tổng kết vào ngày 10 hàng tháng.
            </Typography>
          </Alert>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: dashboardTheme.gradients.secondary,
            color: 'white',
            textAlign: 'center'
          }}>
            <CardContent>
              <WorkHistoryIcon sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                {stats.activeUsers}/{stats.totalUsers}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Nhân viên đang hoạt động
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardSummary; 